/**
 * 镀银浮雕预设动作
 * 
 * 按照以下步骤实现:
 * 第一步：将图层一重命名为"背景层"，将图层二重命名为"黑素层"
 * 第二步：将"背景层"复制一层并重命名为"模糊背景层"，对"模糊背景层"执行高斯模糊4像素
 * 第三步：将"黑素层"复制一层，重命名为"白叠层"，对"白叠层"执行反相，并将混合模式改为叠加
 * 第四步：将"黑素层"图层向右下方移动3个像素并将不透明度改为40%
 * 第五步：将"白叠层"复制一层并重命名为"浮雕层"，将"浮雕层"混合模式改为正常，不透明度100%，并加一个3像素的居外白描边
 * 第六步：将"浮雕层"复制两层重命名为"镀银1"和"镀银2"，将"镀银1"图层明度改为-50并将图层向左上移动2像素，将"镀银2"向右下移动2像素，然后将"浮雕层"和"镀银1""镀银2"三个图层合并，将合并后的图层效果改为叠加
 */

export default function silverEmboss(context) {
  const { layers, layerCanvases, updateLayerThumbnail, redrawCanvas } = context;
  
  console.log('执行镀银浮雕预设...');
  
  try {
    // 确保有足够的图层
    if (layers.length < 2) {
      throw new Error('镀银浮雕预设需要至少两个图片层');
    }
    
    //===== 第一步：重命名图层 =====
    // 将图层一重命名为"背景层"
    layers[0].name = '背景层';
    // 将图层二重命名为"黑素层" 
    layers[1].name = '黑素层';
    
    //===== 第二步：复制背景层并应用高斯模糊 =====
    // 获取背景层的ID和画布
    const bgLayerId = layers[0].id;
    const bgCanvas = layerCanvases[bgLayerId];
    
    // 创建新的图层ID
    const blurBgLayerId = Date.now();
    
    // 创建新的图层对象
    const blurBgLayer = {
      id: blurBgLayerId,
      name: '模糊背景层',
      visible: true,
      opacity: 100,
      blendMode: 'normal',
      filters: {
        blur: 4, // 应用4像素的高斯模糊
        brightness: 100,
        contrast: 100,
        saturation: 100
      }
    };
    
    // 复制背景层的画布内容
    const blurBgCanvas = document.createElement('canvas');
    blurBgCanvas.width = bgCanvas.width;
    blurBgCanvas.height = bgCanvas.height;
    const blurBgCtx = blurBgCanvas.getContext('2d');
    blurBgCtx.drawImage(bgCanvas, 0, 0);
    
    // 添加到图层列表中（在背景层后面）
    layers.splice(1, 0, blurBgLayer);
    
    // 保存画布到图层画布缓存
    layerCanvases[blurBgLayerId] = blurBgCanvas;
    
    // 更新图层缩略图
    updateLayerThumbnail(1); // 模糊背景层
    
    //===== 第三步：复制黑素层并创建白叠层 =====
    // 获取黑素层的ID和画布（现在是第三层）
    const blackLayerId = layers[2].id;
    const blackCanvas = layerCanvases[blackLayerId];
    
    // 创建新的图层ID
    const whiteLayerId = Date.now() + 1;
    
    // 创建白叠层图层对象
    const whiteLayer = {
      id: whiteLayerId,
      name: '白叠层',
      visible: true,
      opacity: 100,
      blendMode: 'overlay', // 混合模式设为叠加
      filters: {
        blur: 0,
        brightness: 100,
        contrast: 100,
        saturation: 100
      }
    };
    
    // 复制黑素层的画布内容
    const whiteCanvas = document.createElement('canvas');
    whiteCanvas.width = blackCanvas.width;
    whiteCanvas.height = blackCanvas.height;
    const whiteCtx = whiteCanvas.getContext('2d');
    whiteCtx.drawImage(blackCanvas, 0, 0);
    
    // 对白叠层执行反相操作
    const imageData = whiteCtx.getImageData(0, 0, whiteCanvas.width, whiteCanvas.height);
    const data = imageData.data;
    
    // 反相处理
    for (let i = 0; i < data.length; i += 4) {
      data[i] = 255 - data[i];         // 红
      data[i + 1] = 255 - data[i + 1]; // 绿
      data[i + 2] = 255 - data[i + 2]; // 蓝
      // 不修改alpha通道
    }
    
    whiteCtx.putImageData(imageData, 0, 0);
    
    // 添加到图层列表中
    layers.splice(3, 0, whiteLayer);
    
    // 保存画布到图层画布缓存
    layerCanvases[whiteLayerId] = whiteCanvas;
    
    // 更新图层缩略图
    updateLayerThumbnail(3); // 白叠层
    
    //===== 第四步：调整黑素层位置和透明度 =====
    // 将黑素层向右下移动3像素
    const blackCtx = blackCanvas.getContext('2d');
    
    // 创建临时画布保存原内容
    const tempBlackCanvas = document.createElement('canvas');
    tempBlackCanvas.width = blackCanvas.width;
    tempBlackCanvas.height = blackCanvas.height;
    const tempBlackCtx = tempBlackCanvas.getContext('2d');
    tempBlackCtx.drawImage(blackCanvas, 0, 0);
    
    // 清除黑素层画布
    blackCtx.clearRect(0, 0, blackCanvas.width, blackCanvas.height);
    
    // 在偏移位置绘制
    blackCtx.drawImage(tempBlackCanvas, 1, 1);
    
    // 调整黑素层不透明度为40%
    layers[2].opacity = 40;
    
    // 更新图层缩略图
    updateLayerThumbnail(2); // 黑素层
    
    //===== 第五步：复制白叠层创建浮雕层 =====
    // 创建新的图层ID
    const embossLayerId = Date.now() + 2;
    
    // 创建浮雕层图层对象
    const embossLayer = {
      id: embossLayerId,
      name: '浮雕层',
      visible: true,
      opacity: 100,
      blendMode: 'normal', // 混合模式设为正常
      filters: {
        blur: 0,
        brightness: 100,
        contrast: 100,
        saturation: 100
      }
    };
    
    // 复制白叠层的画布内容
    const embossCanvas = document.createElement('canvas');
    embossCanvas.width = whiteCanvas.width;
    embossCanvas.height = whiteCanvas.height;
    const embossCtx = embossCanvas.getContext('2d');
    embossCtx.drawImage(whiteCanvas, 0, 0);
    
    // 添加到图层列表
    layers.splice(4, 0, embossLayer);
    
    // 保存画布到图层画布缓存
    layerCanvases[embossLayerId] = embossCanvas;
    
    // 应用3像素白色描边（简化处理）
    embossCtx.strokeStyle = 'white';
    embossCtx.lineWidth = 3;
    embossCtx.strokeRect(2, 2, embossCanvas.width - 6, embossCanvas.height - 6);
    
    // 更新图层缩略图
    updateLayerThumbnail(4); // 浮雕层
    
    //===== 第六步：复制浮雕层创建镀银效果 =====
    // 创建镀银1图层
    const silver1LayerId = Date.now() + 3;
    const silver1Layer = {
      id: silver1LayerId,
      name: '镀银1',
      visible: true,
      opacity: 100,
      blendMode: 'normal',
      filters: {
        blur: 0,
        brightness: 50, // 明度降低50%
        contrast: 100,
        saturation: 100
      }
    };
    
    // 复制浮雕层画布内容
    const silver1Canvas = document.createElement('canvas');
    silver1Canvas.width = embossCanvas.width;
    silver1Canvas.height = embossCanvas.height;
    const silver1Ctx = silver1Canvas.getContext('2d');
    silver1Ctx.drawImage(embossCanvas, 0, 0);
    
    // 创建临时画布保存原内容
    const tempSilver1Canvas = document.createElement('canvas');
    tempSilver1Canvas.width = silver1Canvas.width;
    tempSilver1Canvas.height = silver1Canvas.height;
    const tempSilver1Ctx = tempSilver1Canvas.getContext('2d');
    tempSilver1Ctx.drawImage(silver1Canvas, 0, 0);
    
    // 清除画布
    silver1Ctx.clearRect(0, 0, silver1Canvas.width, silver1Canvas.height);
    
    // 向左上移动2像素
    silver1Ctx.drawImage(tempSilver1Canvas, -0.2, -0.2);
    
    // 添加到图层列表
    layers.splice(5, 0, silver1Layer);
    
    // 保存画布到图层画布缓存
    layerCanvases[silver1LayerId] = silver1Canvas;
    
    // 更新图层缩略图
    updateLayerThumbnail(5); // 镀银1层
    
    // 创建镀银2图层
    const silver2LayerId = Date.now() + 4;
    const silver2Layer = {
      id: silver2LayerId,
      name: '镀银2',
      visible: true,
      opacity: 100,
      blendMode: 'normal',
      filters: {
        blur: 0,
        brightness: 100,
        contrast: 100,
        saturation: 100
      }
    };
    
    // 复制浮雕层画布内容
    const silver2Canvas = document.createElement('canvas');
    silver2Canvas.width = embossCanvas.width;
    silver2Canvas.height = embossCanvas.height;
    const silver2Ctx = silver2Canvas.getContext('2d');
    silver2Ctx.drawImage(embossCanvas, 0, 0);
    
    // 创建临时画布保存原内容
    const tempSilver2Canvas = document.createElement('canvas');
    tempSilver2Canvas.width = silver2Canvas.width;
    tempSilver2Canvas.height = silver2Canvas.height;
    const tempSilver2Ctx = tempSilver2Canvas.getContext('2d');
    tempSilver2Ctx.drawImage(silver2Canvas, 0, 0);
    
    // 清除画布
    silver2Ctx.clearRect(0, 0, silver2Canvas.width, silver2Canvas.height);
    
    // 向右下移动2像素
    silver2Ctx.drawImage(tempSilver2Canvas, 0.5, 0.5);
    
    // 添加到图层列表
    layers.splice(6, 0, silver2Layer);
    
    // 保存画布到图层画布缓存
    layerCanvases[silver2LayerId] = silver2Canvas;
    
    // 更新图层缩略图
    updateLayerThumbnail(6); // 镀银2层
    
    // 合并三个图层：浮雕层、镀银1和镀银2
    // 创建合并后的图层ID
    const mergedLayerId = Date.now() + 5;
    
    // 创建合并后的图层对象
    const mergedLayer = {
      id: mergedLayerId,
      name: '镀银浮雕效果',
      visible: true,
      opacity: 100,
      blendMode: 'overlay', // 混合模式设为叠加
      filters: {
        blur: 0,
        brightness: 100,
        contrast: 100,
        saturation: 100
      }
    };
    
    // 创建合并后的画布
    const mergedCanvas = document.createElement('canvas');
    mergedCanvas.width = embossCanvas.width;
    mergedCanvas.height = embossCanvas.height;
    const mergedCtx = mergedCanvas.getContext('2d');
    
    // 先绘制浮雕层
    mergedCtx.drawImage(embossCanvas, 0, 0);
    
    // 再绘制镀银1层
    mergedCtx.globalAlpha = silver1Layer.opacity / 100;
    mergedCtx.drawImage(silver1Canvas, 0, 0);
    
    // 最后绘制镀银2层
    mergedCtx.globalAlpha = silver2Layer.opacity / 100;
    mergedCtx.drawImage(silver2Canvas, 0, 0);
    
    // 重置设置
    mergedCtx.globalAlpha = 1;
    
    // 移除原来的三个图层
    layers.splice(4, 3, mergedLayer);
    
    // 保存合并画布到图层画布缓存
    layerCanvases[mergedLayerId] = mergedCanvas;
    
    // 更新图层缩略图
    updateLayerThumbnail(4); // 镀银浮雕效果层
    
    // 隐藏模糊背景层
    layers[1].visible = false;
    
    // 最终重绘画布
    redrawCanvas();
    
    console.log('镀银浮雕预设执行完成');
    return true;
  } catch (error) {
    console.error('执行镀银浮雕预设时出错:', error);
    return false;
  }
} 